import { useUser } from '@clerk/react'
import { skipToken } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useLocation } from '#router'
import { Button } from '@/components/ui/button'
import { usePageTitle } from '@/hooks/use-document-title'
import {
  forgetStoredApplyToken,
  readStoredApplyToken,
  storeApplyToken,
} from '@/lib/audition/apply-token'
import { INTAKE_CLOSED_MESSAGE } from '@/lib/audition/intake'
import { APPLY_TOKEN_PARAM } from '@/lib/audition/resume-url'
import {
  type SubmissionAddress,
  submissionAddressKey,
} from '@/lib/audition/submission-address'
import { trpc } from '@/lib/trpc'
import { BookingEmbed } from './booking-embed'
import { PickTime } from './pick-time'

/** Stale application/payment links land here; existing fulfillment stays reachable. */
export function IntakeClosed() {
  usePageTitle('Applications closed')
  const { searchParams, navigate } = useLocation()
  const { isSignedIn } = useUser()
  const [token, setToken] = useState(
    () => searchParams.get(APPLY_TOKEN_PARAM) || readStoredApplyToken(),
  )
  useEffect(() => {
    const urlToken = searchParams.get(APPLY_TOKEN_PARAM)
    if (!urlToken) return
    storeApplyToken(urlToken)
    const cleaned = new URL(window.location.href)
    cleaned.searchParams.delete(APPLY_TOKEN_PARAM)
    navigate(cleaned.pathname + cleaned.search, { replace: true })
  }, [navigate, searchParams])
  const application = trpc.audition.myFulfillableApplication.useQuery(
    undefined,
    { enabled: !!isSignedIn && !token },
  )
  const address: SubmissionAddress | null = token
    ? { token }
    : application.data
      ? { submissionId: application.data.id }
      : null

  return (
    <main className="mx-auto max-w-2xl space-y-6 px-4 py-12">
      <h1 className="text-3xl font-semibold">Applications are closed</h1>
      <p>{INTAKE_CLOSED_MESSAGE}</p>
      <p className="text-muted-foreground">
        If you have already paid or have a call arranged, sign in or open the
        link from your email. For help, reply to your most recent email from us.
      </p>
      {token && isSignedIn && (
        <Button
          variant="outline"
          onClick={() => {
            forgetStoredApplyToken()
            setToken(null)
          }}
        >
          Use signed-in application
        </Button>
      )}
      {application.isError && !token && (
        <p role="alert">
          We couldn't load your application.{' '}
          <Button variant="link" onClick={() => application.refetch()}>
            Try again
          </Button>
        </p>
      )}
      {address && (
        <ExistingFulfillment
          key={submissionAddressKey(address)}
          address={address}
        />
      )}
    </main>
  )
}

function ExistingFulfillment({ address }: { address: SubmissionAddress }) {
  const application = trpc.audition.get.useQuery(address)
  const embed = trpc.audition.bookingEmbed.useQuery(
    application.data?.canFulfill ? address : skipToken,
  )
  const [booked, setBooked] = useState(false)
  if (application.isPending)
    return <p role="status">Loading your application…</p>
  if (application.isError)
    return (
      <p role="alert">
        We couldn't load this application. Your email link may have expired.
        Sign in or reply to your most recent email from us for help.
        <Button variant="link" onClick={() => application.refetch()}>
          Try again
        </Button>
      </p>
    )
  if (!application.data.canFulfill)
    return (
      <p>
        Your existing application record has been kept. If a recent payment is
        still processing, you can check again.{' '}
        <Button variant="link" onClick={() => application.refetch()}>
          Refresh status
        </Button>
      </p>
    )
  return (
    <section className="space-y-4" aria-labelledby="fulfillment-title">
      <h2 id="fulfillment-title" className="text-xl font-semibold">
        Your existing application
      </h2>
      <p>
        Use your confirmation email to manage an existing booking. If you still
        need to arrange your call, you can do so below.
      </p>
      <PickTime address={address} />
      {embed.isError && (
        <p role="alert">
          We couldn't load booking.{' '}
          <Button variant="link" onClick={() => embed.refetch()}>
            Try again
          </Button>
        </p>
      )}
      {booked ? (
        <p role="status">
          Your booking request was sent. Watch your email for confirmation.
        </p>
      ) : (
        embed.data && (
          <BookingEmbed target={embed.data} onBooked={() => setBooked(true)} />
        )
      )}
      {!embed.isPending && !embed.data && !embed.isError && (
        <p>Reply to your most recent email from us to arrange your call.</p>
      )}
    </section>
  )
}
